import { SuspenseLoading } from '@/components/SuspenseErrorBoundary/fallback';
import { ipRestrictionToApiBoolean } from '@/features/ip_address_whitelists/components/RestrictIpSetting/hooks/ipRestrictionRadio';
import {
  type RestrictIpSettingFormValues,
  useRestrictIpSettingForm,
} from '@/features/ip_address_whitelists/components/RestrictIpSetting/hooks/useRestrictIpSettingForm';
import { RestrictIpSetting } from '@/features/ip_address_whitelists/components/RestrictIpSetting/RestrictIpSetting';
import { useIpAddressRestrictionSetting } from '@/features/ip_address_whitelists/IpAddressRestriction/hooks/useIpAddressRestrictionSetting';
import classnames from 'classnames/bind';
import { FC, memo, Suspense, useCallback } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import styles from './IpAddressRestriction.module.scss';

const cx = classnames.bind(styles);

export type IpAddressRestrictionProps = {
  isEditingRestrict: boolean;
  onIsEditingRestrictChange: (isEditing: boolean) => void;
  isWhitelistEmpty?: boolean;
  isRestrictionEditLocked?: boolean;
  setInlineError: (error: Error) => void;
  officeId: string;
};

const InnerIpAddressRestriction: FC<IpAddressRestrictionProps> = memo(
  ({
    isEditingRestrict,
    onIsEditingRestrictChange,
    isWhitelistEmpty = true,
    isRestrictionEditLocked = false,
    setInlineError,
    officeId,
  }) => {
    const { ipRestrictionServerMode, saveIpAddressRestrictionSetting } =
      useIpAddressRestrictionSetting(
        () => onIsEditingRestrictChange(false),
        setInlineError,
        officeId
      );

    const formMethods = useRestrictIpSettingForm(ipRestrictionServerMode);

    const resetIpRestrictionFormToServer = useCallback(() => {
      formMethods.reset({
        ip_address_restriction_enabled: ipRestrictionServerMode,
      });
    }, [formMethods, ipRestrictionServerMode]);

    const handleCancel = useCallback(() => {
      resetIpRestrictionFormToServer();
      onIsEditingRestrictChange(false);
    }, [onIsEditingRestrictChange, resetIpRestrictionFormToServer]);

    const handleSave = useCallback<SubmitHandler<RestrictIpSettingFormValues>>(
      async ({ ip_address_restriction_enabled }) => {
        const isIpRestrictionSaveSuccessful =
          await saveIpAddressRestrictionSetting(ip_address_restriction_enabled);
        if (!isIpRestrictionSaveSuccessful) {
          resetIpRestrictionFormToServer();
        }
      },
      [resetIpRestrictionFormToServer, saveIpAddressRestrictionSetting]
    );

    return (
      <div className={cx(styles['contents'])}>
        <RestrictIpSetting
          formMethods={formMethods}
          isIpAddressRestrictionEnabled={ipRestrictionToApiBoolean(
            ipRestrictionServerMode
          )}
          isEditing={isEditingRestrict}
          isWhitelistEmpty={isWhitelistEmpty}
          isHeaderActionDisabled={isRestrictionEditLocked}
          onClickEdit={() => onIsEditingRestrictChange(true)}
          onClickCancel={handleCancel}
          onSave={handleSave}
        />
      </div>
    );
  }
);
InnerIpAddressRestriction.displayName = 'InnerIpAddressRestriction';

export const IpAddressRestriction: FC<IpAddressRestrictionProps> = memo(
  (props) => (
    <Suspense fallback={<SuspenseLoading />}>
      <InnerIpAddressRestriction {...props} />
    </Suspense>
  )
);
IpAddressRestriction.displayName = 'IpAddressRestriction';
